import cn from 'classnames';
import { ReactNode, useCallback, useMemo } from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';

import type { BaseRowData } from '@adv-frontend/data-provider';

import type { GridCell, GridRow, GridTable, GridVisualOptions, LoadMoreRowsRowProps } from '../../../types';

import styles from '../Grid.module.css';
import { RowMeasurer } from '../RowMeasurer/RowMeasurer';
import { LoadMoreRowCells } from '../LoadMoreRowCells/LoadMoreRowCells';

/**
 * Пропсы компонента `LoadMoreRowsRow`.
 *
 * @typeParam RowData Данные грида.
 */
export type LoadMoreRowsRowWrapperProps<RowData extends BaseRowData> = {
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Инстанс строки.
     */
    row: GridRow<RowData>;
    /**
     * Виртуальная строка.
     */
    virtualRow: VirtualItem;
    /**
     * Функция пересчета виртуализации при изменении размеров строки
     */
    measure: (node: HTMLDivElement | null) => void;
    /**
     * Фиксированная высота строки.
     */
    strictHeight?: number;
    /**
     * Скрывать ли нижний бордер у строки
     */
    hideBottomBorder: boolean;
    /**
     * Коллбек клика по кнопке "загрузить еще"
     */
    onLoadMore: (parentId?: string) => void;
    /**
     * Количество строк в гриде
     */
    totalCount: number;
    /**
     * Количество загруженных строк
     */
    loadedCount: number;
    /**
     * Количество подстрок для каждой строки
     */
    subRowsTotalCount: Record<string, number>;
    /**
     * Количество отображаемых подстрок для каждой строки
     */
    subRowsLoadedCount: Record<string, number>;
    /**
     * Размер чанка строк
     */
    chunkSize: number;
    /**
     * Метод отрисовки строки дозагрузки строк
     * @param props параметры строки дозагрузки
     * @returns Строка для дозагрузки строк
     */
    renderLoadMoreRow: (props: LoadMoreRowsRowProps<RowData>) => ReactNode;
    /**
     * Виртуальные столбцы.
     */
    virtualColumns: VirtualItem[];
    /**
     * Параметры выделения строк.
     */
    rowHighlight?: GridVisualOptions['rowHighlight'];
};

/**
 * Компонент строки "загрузить еще" для грида.
 *
 * @typeParam RowData Данные грида.
 **/
export function LoadMoreRowsRowWrapper<RowData extends BaseRowData>({
    grid,
    row,
    virtualRow,
    measure,
    strictHeight,
    hideBottomBorder,
    onLoadMore,
    totalCount,
    loadedCount,
    subRowsTotalCount,
    subRowsLoadedCount,
    chunkSize,
    renderLoadMoreRow,
    virtualColumns,
    rowHighlight,
}: LoadMoreRowsRowWrapperProps<RowData>) {
    const inlineStyles = useMemo(
        () => ({ top: virtualRow.start, height: strictHeight }),
        [virtualRow.start, strictHeight]
    );
    const { byHover = true } = rowHighlight ?? {};
    const classes = cn(
        styles.row,
        byHover ? styles.rowHighlightedByHover : undefined,
        hideBottomBorder ? null : styles.withBorder
    );
    const contentClasses = cn(styles.rowHightlighter);

    const loadMore = useCallback(() => {
        if (row.depth > 0) {
            onLoadMore(row.parentId);
        } else {
            onLoadMore();
        }
    }, [onLoadMore, row.depth, row.parentId]);
    const rowsTotalCount = useMemo(() => {
        if (row.depth > 0 && row.parentId) {
            return subRowsTotalCount[row.parentId] ?? chunkSize;
        }

        return totalCount;
    }, [chunkSize, row.depth, row.parentId, subRowsTotalCount, totalCount]);
    const rowsLoadedCount = useMemo(() => {
        if (row.depth > 0 && row.parentId) {
            return subRowsLoadedCount[row.parentId] ?? chunkSize;
        }

        return loadedCount;
    }, [row.depth, row.parentId, loadedCount, subRowsLoadedCount, chunkSize]);
    const renderCells = useCallback(
        ({ renderCellContent }: { renderCellContent: (props: { cell: GridCell<RowData> }) => ReactNode }) => {
            return (
                <LoadMoreRowCells
                    classNames={contentClasses}
                    grid={grid}
                    row={row}
                    renderCellContent={renderCellContent}
                    strictHeight={strictHeight}
                    virtualColumns={virtualColumns}
                    hideBottomBorder={hideBottomBorder}
                />
            );
        },
        [contentClasses, grid, hideBottomBorder, row, strictHeight, virtualColumns]
    );

    const content = renderLoadMoreRow({
        className: contentClasses,
        totalCount: rowsTotalCount,
        loadedCount: rowsLoadedCount,
        chunkSize,
        row,
        loadMore,
        renderCells,
    });

    if (strictHeight) {
        return (
            <div
                className={classes}
                style={inlineStyles}
                data-index={virtualRow.index}
                data-testid={`Grid.LoadMoreRow-${row.id}`}
            >
                {content}
            </div>
        );
    }

    return (
        <RowMeasurer
            measure={measure}
            className={classes}
            style={inlineStyles}
            data-index={virtualRow.index}
            data-testid={`Grid.LoadMoreRow-${row.id}`}
            index={virtualRow.index}
        >
            {content}
        </RowMeasurer>
    );
}
